import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ActionSheet, Button, Card, Dialog, List, NavBar, Skeleton, Toast } from 'antd-mobile';
import { CaretRightFilled } from '@ant-design/icons';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {DeliveringPath, DeliveryDetailPath} from "../../../enums/DeliveryNavigatePathEnum";
import EmptyState from "../../../components/EmptyState";
import {useCustomToast} from 'hooks/useCustomToast';
import {useDeliveryTaskOrder, useExceptionDictionaries} from "../store/deliveryStore";
import { MapBoxPageMarker } from '../../../constants/mapBoxTrackLog';
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {formatLocalizedDateTime} from "../../../utils/dateFormatUtils";
import {ExceptionDictionaryType} from '../graphql/queries/getExceptionDictionaryForDelivery';
import {t} from "../../../i18n";
import {useTranslation} from "react-i18next";
import { copyToClipboard } from 'utils/copyToClipboard';
import { useNetwork } from 'hooks/useNetwork';
import { getExceptionTypeText } from '../../../utils/i18nUtils';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import { isAppVersionGreaterThanOrEqual, kNativeScanVersion } from '../../../utils/versionUtils';
import { openNativeScan } from '../../../utils/appRuntime';
import { getOrderNoteContent } from '../../../utils/orderNoteUtils';
import MapboxDetailView from "./components/MapboxDetailView";
import AccessInfoDisplay, { AccessCode } from './components/AccessInfoDisplay';
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import DeliveryPhoneAndNavigate from "./components/DeliveryPhoneAndNavigate";
import AccessCodeValidationDialog from './components/AccessCodeValidationDialog';
import { useAccessCodes } from './hooks/useAccessCodes';
import { getAndClearShouldCheckPendingCodes } from './utils/accessCodeValidationState';
import { validateAccessCodeLocation } from './utils/accessCodeLocationValidation';
import {getUserInfo} from "../../../utils/auth";
import {useLazyQuery} from '@apollo/client';
import {ANALYZE_ADDRESS_SIMPLE, AnalyzeAddressSimpleResponse} from '../../../graphql/queries/addressQueries';
import AddressLocationIncorrectButton from "./components/AddressLocationIncorrectButton";
import { useBatchConfigEnable } from '../../../graphql/queries/commonConfigQueries';
import {
    GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK,
    GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE
} from '../../../constants/grayConfigKeys';

const OrderDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const deliveryTaskOrderId = location.state?.deliveryTaskOrderId;
    const trackingNumber = location.state?.trackingNumber;
    const activeTab = location.state?.activeTab;
    const fromMapMode = location.state?.fromMapMode || false;
    const {showToast} = useCustomToast();
    const { i18n } = useTranslation();
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const exceptionDictionaries = useExceptionDictionaries();
    const [failedActionSheetVisible, setFailedActionSheetVisible] = useState(false);
    const [addAccessInfoDialogVisible, setAddAccessInfoDialogVisible] = useState(false);
    const { isOnline } = useNetwork();
    const [isValidatingLocation, setIsValidatingLocation] = useState(false);
    // 处理新增Code按钮点击（先校验地址）
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleAddCodeClick = async () => {
        const addressLat = deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined;
        const addressLon = deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined;

        // 延迟显示loading（200ms），如果响应很快则不显示，避免闪烁
        loadingTimeoutRef.current = setTimeout(() => {
            setIsValidatingLocation(true);
        }, 200);

        try {
            await validateAccessCodeLocation({
                addressLat,
                addressLon,
                onValidationPassed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    setAddAccessInfoDialogVisible(true);
                },
                onValidationFailed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                },
                isFormSubmit: false
            });
        } catch (error) {
            // 清除延迟显示loading的定时器
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
            setIsValidatingLocation(false);
            console.error('位置校验失败:', error);
        }
    };
    const [geocodedCoordinates, setGeocodedCoordinates] = useState<{latitude: number, longitude: number} | null>(null);
    const noteWrapperCollapsedRef = useRef<HTMLDivElement | null>(null);
    const noteTextCollapsedRef = useRef<HTMLSpanElement | null>(null);
    const [showNoteIndicator, setShowNoteIndicator] = useState(false);

    // 地址分析相关状态
    const [analyzedAddressLine1, setAnalyzedAddressLine1] = useState<string | undefined>(undefined);
    const [analyzeAddress, { loading: analyzingAddress }] = useLazyQuery<AnalyzeAddressSimpleResponse>(ANALYZE_ADDRESS_SIMPLE);

    // 批量检查灰度配置
    const { isEnabled: getConfigEnabled, loading: loadingGrayConfigs } = useBatchConfigEnable([
        GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK,
        GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE
    ]);

    // 获取各个灰度配置的启用状态
    const isAddressCoordinateFeedbackEnabled = getConfigEnabled(GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK);
    const isNavigationAddressAnalyzeEnabled = getConfigEnabled(GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE);

    // 门禁码管理Hook
    const {
        accessCodes,
        pendingAccessCodes,
        validationDialogVisible,
        setValidationDialogVisible,
        handleValidationComplete,
        checkPendingAccessCodes,
        isLoadingAccessCodes,
        isFeatureEnabled,
        isCheckingFeature
    } = useAccessCodes({
        orderId: deliveryTaskOrder?.orderWithAddress?.order?.id,
        loading,
        addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
        addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined
    });

    // 检测从AccessInfoDetailPage返回，触发验证弹窗
    // 暂时禁用：本期先不考虑反馈弹框功能，但代码保留
    // useEffect(() => {
    //     if (accessCodes.length > 0 && !loading && getAndClearShouldCheckPendingCodes()) {
    //         setTimeout(() => {
    //             checkPendingAccessCodes();
    //         }, 300);
    //     }
    // }, [accessCodes.length, loading, checkPendingAccessCodes]);

    // 当deliveryTaskOrder加载完成后，调用地址分析API
    useEffect(() => {

        if (!deliveryTaskOrder || loading) {
            return;
        }

        const deliveryAddress = deliveryTaskOrder.orderWithAddress?.deliveryAddress;
        console.log("deliveryAddress",deliveryAddress)
        if (!deliveryAddress?.addressLine1 || !deliveryAddress?.city || !deliveryAddress?.state || !deliveryAddress?.zipcode) {
            return;
        }

        // 检查灰度配置，如果未启用则不调用地址分析API
        if (!isNavigationAddressAnalyzeEnabled) {
            return;
        }

        // 调用地址分析API
        analyzeAddress({
            variables: {
                streetAddress: deliveryAddress.addressLine1,
                unit: deliveryAddress.addressLine2 || null,
                city: deliveryAddress.city,
                state: deliveryAddress.state,
                zipCode: deliveryAddress.zipcode
            }
        }).then(response => {
            if (response.data?.analyzeAddressSimple?.entityComponents) {
                const components = response.data.analyzeAddressSimple.entityComponents;
                // 组合标准化解析后的字段：line1 + city + state + zipcode，state 和 zipcode 之间用空格，其他用逗号分隔
                if (components?.addressLine1) {
                    const stateZip = [components.stateAbbreviation, components.zipcode].filter(Boolean).join(' ');
                    const parts = [components.addressLine1, components.cityName, stateZip].filter(Boolean);
                    setAnalyzedAddressLine1(parts.join(', '));
                }
            }
        }).catch(error => {
            console.error('Failed to analyze address:', error);
            // 分析失败不影响主流程，保持静默
        });

    }, [deliveryTaskOrder, loading, analyzeAddress, isNavigationAddressAnalyzeEnabled]);

    // 处理返回逻辑
    const handleBack = () => {
        // 正常返回
        if (fromMapMode) {
            // 如果来自地图模式，则返回到地图模式
            navigate(-1);
        } else {
            // 默认返回到配送页面
            navigate(DeliveringPath.DELIVERY_DELIVERING, { state: { activeTab } });
        }
    };

    const handleDeliverySuccess = async () => {
        const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;

        // 检查版本是否支持原生扫码
        const supportsNativeScan = await isAppVersionGreaterThanOrEqual(kNativeScanVersion);

        if (supportsNativeScan) {
             openNativeScan(currentTrackingNumber, () => {
                navigateOrderDetail(currentTrackingNumber)
             });
        } else {
            if (!isOnline) {
                // 无网情况：直接跳转派送成功页面
                navigateOrderDetail(currentTrackingNumber)
              } else {
                const targetPath = DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', currentTrackingNumber);
                const targetState = {
                    deliveryTaskOrderId: deliveryTaskOrderId,
                    trackingNumber: currentTrackingNumber,
                    detailType: 'delivered'
                };
                navigate(DeliveringPath.DELIVERY_SCAN_PACKAGE, {
                    state: {
                        targetPath: targetPath,
                        targetState: targetState
                    }
                });
            }
        }
    };

    const navigateOrderDetail = (currentTrackingNumber: string) => {
        navigate(DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', currentTrackingNumber),{
        state:{
            deliveryTaskOrderId: deliveryTaskOrderId,
            trackingNumber:currentTrackingNumber,
            detailType: 'delivered'
        }
    });
 }

    const handleFailedActionSheetVisible = async () => {
        if (exceptionDictionaries.length === 0){
            showToast('fail', 'No exception actions available.', 'center');
            return;
        }
        setFailedActionSheetVisible(true);
    };
    
    const handleActionClick = async (action: ExceptionDictionaryType) => {
        setFailedActionSheetVisible(false);
        const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
        navigate(DeliveringPath.DELIVERY_MARK_EXCEPTION,{
            state:{
                deliveryTaskOrderId: deliveryTaskOrderId,
                trackingNumber:currentTrackingNumber,
                exceptionReason: action
            }
        });

    };

    // 处理来自原生的派送成功回调
    const handleDeliverySuccessFromNative = () => {
        handleDeliverySuccess();
    };

    // 处理来自原生的派送失败回调
    const handleDeliveryFailedFromNative = () => {
        handleFailedActionSheetVisible();
    };

    const handleCopyAddress = async () => {
        if (!address || address.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(address);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    };

    const noteContent = useMemo(() => {
        return getOrderNoteContent(deliveryTaskOrder?.orderNote, i18n.language);
    }, [deliveryTaskOrder?.orderNote, i18n.language]);

    useEffect(() => {
        if (!noteContent) {
            setShowNoteIndicator(false);
            return;
        }

        const measure = () => {
            const wrapper = noteWrapperCollapsedRef.current;
            const text = noteTextCollapsedRef.current;
            if (!wrapper || !text) {
                setShowNoteIndicator(false);
                return;
            }
            // 检测文本是否被截断：文本的实际宽度是否大于容器的可用宽度
            // 考虑指示器大约需要 24px 的空间（图标宽度 + gap）
            const indicatorWidth = 24;
            const availableWidth = wrapper.clientWidth - indicatorWidth;
            const truncated = text.scrollWidth > availableWidth;
            setShowNoteIndicator(truncated);
        };

        // 使用 setTimeout 确保 DOM 已经渲染完成
        const timeoutId = setTimeout(() => {
            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                window.requestAnimationFrame(measure);
            } else {
                measure();
            }
        }, 0);

        if (typeof window !== 'undefined') {
            const handleResize = () => {
                setTimeout(() => {
                    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                        window.requestAnimationFrame(measure);
                    } else {
                        measure();
                    }
                }, 0);
            };
            window.addEventListener('resize', handleResize);
            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', handleResize);
            };
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [noteContent]);

    const handleNoteClick = useCallback(() => {
        if (!noteContent) {
            return;
        }

        const trimmed = noteContent.trim();

        let dialogRef: { close: () => void } | null = null;

        const dialogContent = (
            <div className="flex flex-col max-h-[60vh]">
                <div className="flex-1 overflow-y-auto text-left whitespace-pre-wrap pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {trimmed}
                </div>
                <Button
                    color='primary'
                    block
                    className="mt-4 flex-shrink-0"
                    onClick={() => dialogRef?.close()}
                >
                    {t('pages.delivery.orderDetail.noteDialogButton')}
                </Button>
            </div>
        );

        dialogRef = Dialog.show({
            title: t('pages.delivery.orderDetail.note'),
            closeOnMaskClick: true,
            content: dialogContent,
        });
    }, [noteContent, t]);

    if (loading){
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {trackingNumber || t('pages.delivery.orderDetail.loading')}
                    </NavBar>
                </div>
                <Skeleton style={{width: '100%',height: '45%'}} animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
            </div>
        );
    }

    if (!deliveryTaskOrder) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {trackingNumber || 'Unknown'}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    const address = [
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
        'US'
    ].filter(Boolean).join(' ');
    const stopNumber = deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0';
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phoneNumberParts = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber?.split(',') || [];
    const phone = phoneNumberParts[0] || deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const extension = phoneNumberParts[1] ? phoneNumberParts[1].replace('#', '') : deliveryTaskOrder.orderWithAddress?.deliveryAddress?.extension;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;
    const longitude = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.longitude;
    const latitude = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.latitude;
    const userInfo = getUserInfo();

    return (
        <div className="flex flex-col h-screen relative">
            {/* 位置校验Loading遮罩 */}
            {isValidatingLocation && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"/>
                            <div className="text-gray-700 text-base">{t('pages.delivery.orderDetail.loading')}</div>
                        </div>
                    </div>
                </div>
            )}
            {/* Navbar */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>
                    {deliveryTaskOrder?.trackingNumber || trackingNumber}
                </NavBar>
            </div>

            {/* Map */}
            <div className="h-2/5">
                {deliveryTaskOrder && <MapboxDetailView stopNumber={stopNumber || 1} address={address} longitude={longitude} latitude={latitude} pageMarker={MapBoxPageMarker.DELIVERY_ORDER_DETAIL} />}
            </div>

            {/* Order Details */}
            <Card className="flex-1 m-2 rounded-lg overflow-y-auto flex flex-col relative">
                {/* Address location incorrect button - top right (only show for whitelisted drivers) */}
                {isAddressCoordinateFeedbackEnabled && (
                    <AddressLocationIncorrectButton
                        deliveryTaskOrderId={deliveryTaskOrderId}
                        trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                        address={address}
                        latitude={latitude}
                        longitude={longitude}
                        geocodedCoordinates={geocodedCoordinates}
                        stopNumber={stopNumber}
                        className="absolute -top-1 right-1 text-sm text-red-600 px-2 py-1 z-10"
                    />
                )}
                <div className="flex justify-between items-center mb-4 gap-3 sm:gap-4">
                    <div className="flex flex-col h-full justify-center items-center flex-shrink-0 min-w-[60px] sm:min-w-[80px] w-[60px] sm:w-20">
                        <p className="text-2xl font-bold whitespace-nowrap">{stopNumber}</p>
                        <p className="text-sm text-gray-500">{t('pages.stop')}</p>
                    </div>
                    <div className="flex flex-col gap-3">
                        {/* 第一组：主-收件人名称；副-手机号 */}
                        <div className="flex flex-col gap-1">
                            <h3 className="text-lg font-medium">{fullName}</h3>
                            <div className="text-sm text-gray-500">{`${maskPhoneNumber(phone)}${extension ? `-${extension}` : ''}`}</div>
                        </div>
                        {/* 第二组：主-运单地址；副-门禁信息 */}
                        <div className="flex flex-col gap-1">
                            <div className="flex-1 text-blue-500 text-base">
                                <div className="flex items-center gap-2">
                                    <span>{address}</span>
                                    <button
                                        onClick={handleCopyAddress}
                                        className="p-1 flex items-center justify-center"
                                        style={{ minWidth: '24px', height: '24px' }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                        </svg>
                                    </button>
                                </div>

                            </div>
                            {/* 门禁信息展示 */}
                            <AccessInfoDisplay
                                accessCodes={accessCodes}
                                addressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id}
                                trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                address={address}
                                addressLat={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined}
                                addressLon={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined}
                                orderId={deliveryTaskOrder?.orderWithAddress?.order?.id}
                                onAddCode={handleAddCodeClick}
                                showAddButton={true}
                                isLoading={isLoadingAccessCodes || isCheckingFeature}
                                fromMapMode={fromMapMode}
                                deliveryTaskOrderId={deliveryTaskOrderId}
                                isFeatureEnabled={isFeatureEnabled}
                            />
                        </div>
                    </div>
                </div>

                <DeliveryPhoneAndNavigate
                    tracking={deliveryTaskOrder?.trackingNumber || trackingNumber}
                    phone={phone || ''}
                    address={address}
                    addressLine1={analyzedAddressLine1}
                    isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                    longitude={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.longitude}
                    latitude={deliveryTaskOrder.orderWithAddress?.deliveryAddress?.latitude}
                    stopNumber={deliveryTaskOrder.stopNumber.toString()}
                    fullName={fullName}
                    subStopNumber={deliveryTaskOrder?.subStopNumber}
                    driverId={parseInt(getUserInfo()!!.account.id)}
                    driverName={getUserInfo()!!.account.name}
                    onDeliverySuccessFromNative={handleDeliverySuccessFromNative}
                    onDeliveryFailedFromNative={handleDeliveryFailedFromNative}
                    orderAddressId={deliveryTaskOrder.orderWithAddress.deliveryAddress.id}
                    dspId={deliveryTaskOrder.dspId}
                />

                <List
                    style={{
                        '--border-top': 'none',
                        '--border-bottom': 'none',
                        '--padding-left' : '0px',
                        '--padding-right' : '0px',
                    }}
                >
                    <List.Item title={`${t('pages.delivery.orderDetail.route')}:`} extra={routeNumber || '-'} />
                    <List.Item title={`${t('pages.delivery.orderDetail.assignedTime')}:`} extra={deliverAt ? formatLocalizedDateTime(timestampToDate(deliverAt)) : ''}/>
                    <List.Item title={`${t('pages.delivery.orderDetail.deliveryAttempt')}:`} extra={`${deliveryAttempts} out of 3`}/>
                    {noteContent && (
                        <List.Item
                            className="cursor-pointer [&_.adm-list-item-content-main]:flex-[0_0_auto] [&_.adm-list-item-content-main]:max-w-xs [&_.adm-list-item-content-extra]:flex-1 [&_.adm-list-item-content-extra]:min-w-0 [&_.adm-list-item-content-extra]:max-w-none [&_.adm-list-item-content-extra]:flex-[1_1_auto]"
                            title={`${t('pages.delivery.orderDetail.note')}:`}
                            arrow={false}
                            clickable
                            onClick={handleNoteClick}
                            extra={
                                <div className="flex flex-row items-center justify-end gap-1.5 text-right min-w-0 flex-1 w-full" ref={noteWrapperCollapsedRef}>
                                    <span
                                        className={showNoteIndicator
                                            ? "inline-block max-w-full whitespace-nowrap overflow-hidden text-ellipsis text-sm text-gray-800 font-semibold flex-[0_1_auto] text-left"
                                            : "text-sm text-gray-800 font-semibold"}
                                        ref={noteTextCollapsedRef}
                                    >
                                        {noteContent}
                                    </span>
                                    {showNoteIndicator && (
                                        <CaretRightFilled className="text-blue-500 font-semibold tracking-wide flex-shrink-0 flex items-center justify-center" />
                                    )}
                                </div>
                            }
                        />
                    )}
                </List>
            </Card>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    color='default'
                    onClick={handleFailedActionSheetVisible}
                    block
                    style={{
                        '--background-color': 'gray',
                        '--text-color': 'white'
                    }}
                >
                    {t('pages.delivery.orderDetail.failed')}
                </Button>
                <Button
                    color='primary'
                    onClick={handleDeliverySuccess}
                    block
                >
                    {t('pages.delivery.orderDetail.delivered')}
                </Button>
            </div>

            <ActionSheet
                visible={failedActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.delivery.orderDetail.failed')}</h3>
                        <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => setFailedActionSheetVisible(false)}
                actions={exceptionDictionaries.map(action => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getExceptionTypeText(action)}
                            </div>
                        </div>
                    ),
                    key: action.exceptionReasonCode,
                    onClick: () => handleActionClick(action)
                }))}
            />

            {/* 新增门禁信息确认弹窗 */}
            <Dialog
                visible={addAccessInfoDialogVisible}
                content={
                    <div className="py-2">
                        <div className="text-base font-semibold mb-3 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoTitle')}
                        </div>
                        <div className="text-base mb-4 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoConfirm')}
                        </div>
                    </div>
                }
                actions={[
                    [
                        {
                            key: 'cancel',
                            text: t('pages.delivery.orderDetail.cancel'),
                            onClick: () => setAddAccessInfoDialogVisible(false),
                            style: { color: '#4b5563' }
                        },
                        {
                            key: 'confirm',
                            text: t('pages.delivery.orderDetail.confirm'),
                            style: { color: '#60a5fa' },
                            onClick: () => {
                                setAddAccessInfoDialogVisible(false);
                                // 跳转到新增code页面
                                const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
                                navigate(
                                    DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                                        ':trackingNumber',
                                        currentTrackingNumber
                                    ),
                                    {
                                        state: {
                                            addressId: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id,
                                            trackingNumber: currentTrackingNumber,
                                            address: address,
                                            addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
                                            addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined,
                                            orderId: deliveryTaskOrder?.orderWithAddress?.order?.id // 传递订单ID
                                        }
                                    }
                                );
                            }
                        }
                    ]
                ]}
                onClose={() => setAddAccessInfoDialogVisible(false)}
            />

            {/* 有效性确认弹窗 */}
            <AccessCodeValidationDialog
                visible={validationDialogVisible}
                pendingAccessCodes={pendingAccessCodes}
                onValidationComplete={handleValidationComplete}
                onClose={() => setValidationDialogVisible(false)}
            />
        </div>
    );
};

export default OrderDetailPage;